import { ChangeDetectionStrategy, Component, computed, signal } from '@angular/core';

const C4_COLS = 7, C4_ROWS = 6;

/**
 * Connect Four — hotseat 2-player drop game on a 7×6 board, win on 4-in-a-row.
 * Ported from the OTA-B2B-6 design (games2.jsx). Default (Emulated) encapsulation
 * keeps its `c4-*` CSS scoped — no clash with the Entertainment Connect Four.
 */
@Component({
  selector: 'app-mini-connect-four',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './mini-connect-four.component.html',
  styleUrl: './mini-connect-four.component.scss',
})
export class MiniConnectFourComponent {
  readonly cols = C4_COLS;
  readonly rows = C4_ROWS;
  readonly cellCount = C4_COLS * C4_ROWS;

  readonly grid = signal<number[]>(Array(C4_COLS * C4_ROWS).fill(0));
  readonly turn = signal(1);
  readonly winner = signal(0);

  readonly cells = computed(() => {
    const g = this.grid();
    const w = this.winner();
    const out: { i: number; c: number; v: number; topEmpty: boolean }[] = [];
    for (let i = 0; i < C4_COLS * C4_ROWS; i++) {
      const r = Math.floor(i / C4_COLS), c = i % C4_COLS;
      out.push({ i, c, v: g[this.idx(r, c)], topEmpty: this.topEmpty(c) === r && !w });
    }
    return out;
  });

  private idx(r: number, c: number): number { return r * C4_COLS + c; }

  private checkWin(g: number[], p: number): boolean {
    const dirs = [[0, 1], [1, 0], [1, 1], [1, -1]];
    for (let r = 0; r < C4_ROWS; r++) for (let c = 0; c < C4_COLS; c++) {
      if (g[this.idx(r, c)] !== p) continue;
      for (const [dr, dc] of dirs) {
        let n = 1, rr = r + dr, cc = c + dc;
        while (rr >= 0 && rr < C4_ROWS && cc >= 0 && cc < C4_COLS && g[this.idx(rr, cc)] === p) { n++; rr += dr; cc += dc; }
        if (n >= 4) return true;
      }
    }
    return false;
  }

  drop(c: number): void {
    if (this.winner()) return;
    let row = -1;
    for (let r = C4_ROWS - 1; r >= 0; r--) if (this.grid()[this.idx(r, c)] === 0) { row = r; break; }
    if (row < 0) return;
    const turn = this.turn();
    const ng = this.grid().slice(); ng[this.idx(row, c)] = turn; this.grid.set(ng);
    if (this.checkWin(ng, turn)) this.winner.set(turn);
    else if (ng.every((v) => v)) this.winner.set(-1);
    else this.turn.set(turn === 1 ? 2 : 1);
  }

  reset(): void {
    this.grid.set(Array(C4_COLS * C4_ROWS).fill(0));
    this.turn.set(1);
    this.winner.set(0);
  }

  private topEmpty(c: number): number {
    for (let r = 0; r < C4_ROWS; r++) if (this.grid()[this.idx(r, c)]) return r - 1;
    return C4_ROWS - 1;
  }
}
